import { useUnAuthStackNavigation } from '@/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm } from 'react-hook-form';

import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useCheckMethodsForEmail, useLookupUserAccount } from '@/features/auth/queries';
import type { EnterEmailFormValues } from '@/features/auth/schema';
import { enterEmailSchema } from '@/features/auth/schema';
import { createDefaultPassword } from '@/features/auth/utils/helpers';
import { captureException } from '@/libs/logger';
import { useUiStore } from '@/stores';
import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';

export function useEnterEmail() {
  const unAuthStackNavigation = useUnAuthStackNavigation();
  // const rootNavigation = useRootNavigation();
  const { mutateAsync: checkMethodsForEmail } = useCheckMethodsForEmail();
  const { mutateAsync: lookupUserAccount } = useLookupUserAccount();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<EnterEmailFormValues>({
    resolver: yupResolver(enterEmailSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });

  const {
    field: { value: email, onChange: onChangeEmail, onBlur: onBlurEmail },
  } = useController({
    name: 'email',
    control,
  });

  const handleNext = useCallback(
    async (_data: EnterEmailFormValues) => {
      try {
        showGlobalLoading();
        const methods = await checkMethodsForEmail(_data.email);
        if (methods.length > 0) {
          const userAccount = await lookupUserAccount(_data.email);
          if (userAccount.emailVerified) {
            unAuthStackNavigation.navigate('EnterPassword', { email: _data.email });
          } else {
            const defaultPass = createDefaultPassword(userAccount.name, new Date(userAccount.date));
            unAuthStackNavigation.navigate('VerifyEmail', {
              email: _data.email,
              defaultPass,
            });
          }
        } else {
          showAlert({
            title: STRINGS.auth.enterEmail.alertTitle,
            message: STRINGS.auth.enterEmail.alertEmailNotApproved,
          });
        }
      } catch (error) {
        captureException(
          {
            error_payload: error,
          },
          { tags: { usk_log_type: 'next_enter_email' } },
        );
      } finally {
        hideGlobalLoading();
      }
    },
    [
      checkMethodsForEmail,
      hideGlobalLoading,
      lookupUserAccount,
      showGlobalLoading,
      unAuthStackNavigation,
    ],
  );

  const handleFormSubmit = handleSubmit(handleNext, invalidErrors => {
    const emailError = invalidErrors.email?.message ?? STRINGS.auth.enterEmail.errorCheckEmail;
    showAlert({
      title: STRINGS.auth.enterEmail.noticeTitle,
      message: emailError,
    });
  });

  return {
    email,
    onChangeEmail,
    onBlurEmail,
    errors,
    isValid,
    isSubmitting,
    handleSubmit: handleFormSubmit,
    handleNext,
  };
}
